import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import Loader from "../Loader";
import {
  PaymentService,
  UpdatePaymentTransactionPayload,
  PaymentTransaction,
} from "../../api/payment";

interface EditTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transaction: PaymentTransaction;
}

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  transaction,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UpdatePaymentTransactionPayload>({
    amountReceived: transaction.amountReceived || 0,
    addComment: transaction.addComment || "",
    paymentDate: transaction.paymentDate
      ? new Date(transaction.paymentDate).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
  });
  const [errors, setErrors] = useState<{
    amountReceived?: string;
    paymentDate?: string;
  }>({});

  useEffect(() => {
    if (transaction) {
      setFormData({
        amountReceived: transaction.amountReceived || 0,
        addComment: transaction.addComment || "",
        paymentDate: transaction.paymentDate
          ? new Date(transaction.paymentDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    }
  }, [transaction]);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (formData.amountReceived <= 0) {
      newErrors.amountReceived = "Amount must be greater than 0";
    }

    if (!formData.paymentDate) {
      newErrors.paymentDate = "Payment date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await PaymentService.updateTransaction(
        transaction.id,
        formData
      );
      const body = res?.data || {};
      const statusCode = body.statusCode ?? body.code;
      const message =
        body.message || "Transaction updated successfully!";

      if (statusCode && statusCode !== 200) {
        toast.error(message);
        return;
      }

      toast.success(message);
      onSuccess();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to update transaction"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: UpdatePaymentTransactionPayload) => ({
      ...prev,
      [name]:
        name === "amountReceived"
          ? parseFloat(value) || 0
          : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-4"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-10">
                <Loader />
              </div>
            )}

            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Edit Payment Transaction
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Amount Received */}
              <div>
                <label className="block mb-1 text-sm font-medium required-star">
                  Amount Received
                </label>
                <input
                  type="number"
                  name="amountReceived"
                  value={formData.amountReceived}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  required
                  className="w-full"
                  placeholder="0.00"
                />
                {errors.amountReceived && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.amountReceived}
                  </p>
                )}
              </div>

              {/* Payment Date */}
              <div>
                <label className="block mb-1 text-sm font-medium required-star">
                  Payment Date
                </label>
                <input
                  type="date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
                {errors.paymentDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.paymentDate}
                  </p>
                )}
              </div>

              {/* Comment */}
              <div>
                <label className="block mb-1 text-sm font-medium">
                  Comment
                </label>
                <textarea
                  name="addComment"
                  value={formData.addComment}
                  onChange={handleChange}
                  rows={3}
                  className="w-full"
                  placeholder="Add any additional comments..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 rounded-lg bg-[#016683] text-white hover:opacity-90 transition"
                  disabled={loading}
                >
                  Update Transaction
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditTransactionModal;

